import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { KeyFileOpener, NewKeySteps, deploymentWarning, keyErrorLine } from './KeyFileDialogs';
import { bytesToBase64 } from '../../utils/base64';
import { createProofMessage, ed25519Available, fingerprintOf, replaceProofMessage, rotationStatementBytes, type SigningKey } from './releaseKeyCrypto';
import type { ReleaseKey } from './types';

const BASE = '/api/backend/kiosk-release-keys';
const REASON_MIN = 20;
const REASON_MAX = 500;
const when = (iso: string | null) => (iso === null ? '-' : new Date(iso).toLocaleString());
const person = (id: string | null, email: string | null) => (email !== null ? email : id === null ? '-' : id);

type DialogKind = 'create' | 'rotate' | 'replace' | 'check';

const Fingerprint = ({ value }: { value: string }) => (
  <Typography sx={{ fontFamily: 'monospace', fontSize: { xs: '1.25rem', md: '1.75rem' }, letterSpacing: '0.05em', wordBreak: 'break-word' }}>{value}</Typography>
);

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
  </Box>
);

interface DialogProps {
  onClose: () => void;
  onChanged: () => void;
}

const DoneStep = ({ line, fingerprint, keyId }: { line: string; fingerprint: string; keyId: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Alert severity="success">{line}</Alert>
    <Fingerprint value={fingerprint} />
    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{keyId}</Typography>
  </Box>
);

interface FrameProps {
  title: string;
  busy: boolean;
  pending: boolean;
  onClose: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

const KeyDialogFrame = ({ title, busy, pending, onClose, children, actions }: FrameProps) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [confirming, setConfirming] = useState(false);
  const requestClose = () => {
    if (busy) return;
    if (pending) {
      setConfirming(true);
      return;
    }
    onClose();
  };
  return (
    <Dialog open onClose={requestClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {children}
          {confirming && <Alert severity="warning">{t('closeConfirm.line')}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        {confirming ? (
          <>
            <Button onClick={() => setConfirming(false)} sx={{ textTransform: 'none' }}>{t('closeConfirm.keep')}</Button>
            <Button color="error" onClick={onClose} sx={{ textTransform: 'none' }}>{t('closeConfirm.close')}</Button>
          </>
        ) : (
          <>
            <Button onClick={requestClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('actions.close')}</Button>
            {actions}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const CreateDialog: React.FC<DialogProps> = ({ onClose, onChanged }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [next, setNext] = useState<SigningKey | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [created, setCreated] = useState<ReleaseKey | null>(null);
  const register = async () => {
    if (next === null) return;
    const key = next;
    setNext(null);
    setBusy(true);
    setProblem(null);
    try {
      const proofB64 = await key.sign(createProofMessage(key.keyId, key.publicKeyB64));
      const answer = await apiClient.post<{ data: ReleaseKey }>(BASE, { publicKeyB64: key.publicKeyB64, proofB64 });
      setCreated(answer.data);
    } catch (error) {
      setProblem(keyErrorLine(error, translate));
    } finally {
      setBusy(false);
      onChanged();
    }
  };
  return (
    <KeyDialogFrame
      title={t('actions.create')}
      busy={busy}
      pending={downloaded && created === null}
      onClose={onClose}
      actions={created === null && next !== null && <Button variant="contained" disabled={busy} onClick={register} sx={{ textTransform: 'none' }}>{t('actions.register')}</Button>}
    >
      {created !== null ? (
        <DoneStep line={t('create.card')} fingerprint={created.fingerprint} keyId={created.keyId} />
      ) : (
        <>
          <NewKeySteps ready={next !== null} onReady={setNext} onDownloaded={() => setDownloaded(true)} />
          {problem !== null && <Alert severity="error">{problem}</Alert>}
        </>
      )}
    </KeyDialogFrame>
  );
};

const RotateDialog: React.FC<DialogProps & { active: ReleaseKey }> = ({ active, onClose, onChanged }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [current, setCurrent] = useState<SigningKey | null>(null);
  const [currentLoaded, setCurrentLoaded] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [next, setNext] = useState<SigningKey | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [rotated, setRotated] = useState<ReleaseKey | null>(null);
  const register = async () => {
    if (current === null || next === null) return;
    const previousKey = current;
    const nextKey = next;
    setCurrent(null);
    setNext(null);
    setBusy(true);
    setProblem(null);
    try {
      const bytes = rotationStatementBytes(previousKey, nextKey, new Date());
      const signatureB64 = await previousKey.sign(bytes);
      const newKeyProofB64 = await nextKey.sign(bytes);
      const answer = await apiClient.post<{ data: ReleaseKey }>(`${BASE}/rotate`, { statementB64: bytesToBase64(bytes), signatureB64, newKeyProofB64 });
      setRotated(answer.data);
    } catch (error) {
      setProblem(keyErrorLine(error, translate));
    } finally {
      setBusy(false);
      onChanged();
    }
  };
  return (
    <KeyDialogFrame
      title={t('actions.rotate')}
      busy={busy}
      pending={downloaded && rotated === null}
      onClose={onClose}
      actions={rotated === null && current !== null && next !== null && <Button variant="contained" disabled={busy} onClick={register} sx={{ textTransform: 'none' }}>{t('actions.register')}</Button>}
    >
      {rotated !== null ? (
        <DoneStep line={t('rotate.done')} fingerprint={rotated.fingerprint} keyId={rotated.keyId} />
      ) : (
        <>
          <Typography variant="body2">{t('rotate.intro')}</Typography>
          {current === null && (
            <KeyFileOpener
              fileTestId="current-file"
              passwordTestId="current-password"
              fileLabel={t('rotate.currentFile')}
              passwordLabel={t('rotate.currentPassword')}
              onOpened={(key, deployment) => {
                if (key.keyId !== active.keyId) return 'not_active_key';
                setWarning(deploymentWarning(deployment, translate));
                setCurrent(key);
                setCurrentLoaded(true);
                return null;
              }}
            />
          )}
          {warning !== null && <Alert severity="warning">{warning}</Alert>}
          {currentLoaded && <NewKeySteps ready={next !== null} onReady={setNext} onDownloaded={() => setDownloaded(true)} />}
          {problem !== null && <Alert severity="error">{problem}</Alert>}
        </>
      )}
    </KeyDialogFrame>
  );
};

const ReplaceDialog: React.FC<DialogProps & { active: ReleaseKey }> = ({ active, onClose, onChanged }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [target] = useState(active);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [next, setNext] = useState<SigningKey | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [replaced, setReplaced] = useState<ReleaseKey | null>(null);
  const trimmed = reason.trim();
  const confirmed = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX && confirm === 'REPLACE';
  const register = async () => {
    if (next === null || stale) return;
    const key = next;
    setNext(null);
    setBusy(true);
    setProblem(null);
    try {
      const proofB64 = await key.sign(replaceProofMessage(key.keyId, key.publicKeyB64));
      const answer = await apiClient.post<{ data: ReleaseKey }>(`${BASE}/replace`, { replacesKeyId: target.keyId, publicKeyB64: key.publicKeyB64, proofB64, reason: trimmed, confirm: 'REPLACE' });
      setReplaced(answer.data);
    } catch (error) {
      if ((error as ApiError).errorCode === 'KIOSK_RELEASE_KEY_STALE') setStale(true);
      else setProblem(keyErrorLine(error, translate));
    } finally {
      setBusy(false);
      onChanged();
    }
  };
  return (
    <KeyDialogFrame
      title={t('actions.replace')}
      busy={busy}
      pending={downloaded && replaced === null && !stale}
      onClose={onClose}
      actions={replaced === null && !stale && next !== null && <Button variant="contained" color="error" disabled={busy || !confirmed} onClick={register} sx={{ textTransform: 'none' }}>{t('actions.register')}</Button>}
    >
      {replaced !== null ? (
        <DoneStep line={t('create.card')} fingerprint={replaced.fingerprint} keyId={replaced.keyId} />
      ) : (
        <>
          <Alert severity="warning">{t('replace.warning')}</Alert>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{translate('resources.kiosk-release-keys.replace.target', { fingerprint: target.fingerprint })}</Typography>
          {stale ? (
            <Alert severity="error">{t('replace.stale')}</Alert>
          ) : (
            <>
              <TextField label={t('replace.reason')} value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={2} inputProps={{ 'data-testid': 'replace-reason', maxLength: REASON_MAX }} />
              <TextField label={t('replace.confirm')} value={confirm} onChange={(event) => setConfirm(event.target.value)} inputProps={{ 'data-testid': 'replace-confirm' }} />
              <NewKeySteps disabled={!confirmed} ready={next !== null} onReady={setNext} onDownloaded={() => setDownloaded(true)} />
              {problem !== null && <Alert severity="error">{problem}</Alert>}
            </>
          )}
        </>
      )}
    </KeyDialogFrame>
  );
};

type CheckResult = { kind: 'active' | 'unknown'; keyId: string; fingerprint: string; warning: string | null } | { kind: 'earlier'; keyId: string; fingerprint: string; status: string; warning: string | null };

const CheckDialog: React.FC<{ keys: ReleaseKey[] | null; problem: string | null; onClose: () => void }> = ({ keys, problem, onClose }) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) => translate(`resources.kiosk-release-keys.${key}`, options);
  const [result, setResult] = useState<CheckResult | null>(null);
  return (
    <KeyDialogFrame title={t('actions.checkFile')} busy={false} pending={false} onClose={onClose}>
      <Typography variant="body2">{t('check.intro')}</Typography>
      {keys === null && problem !== null && <Alert severity="error">{problem}</Alert>}
      <KeyFileOpener
        fileTestId="check-key-file"
        disabled={keys === null}
        passwordTestId="check-key-password"
        fileLabel={translate('resources.kiosk-releases.upload.keyFile')}
        passwordLabel={translate('resources.kiosk-releases.upload.password')}
        onOpened={(key, deployment) => {
          if (keys === null) return null;
          const known = keys.find((candidate) => candidate.keyId === key.keyId && candidate.publicKeyB64 === key.publicKeyB64);
          const warning = deploymentWarning(deployment, translate);
          if (known === undefined) setResult({ kind: 'unknown', keyId: key.keyId, fingerprint: fingerprintOf(key.keyId), warning });
          else if (known.status === 'active') setResult({ kind: 'active', keyId: known.keyId, fingerprint: known.fingerprint, warning });
          else setResult({ kind: 'earlier', keyId: known.keyId, fingerprint: known.fingerprint, status: t(`statuses.${known.status}`), warning });
          return null;
        }}
        onFailed={() => setResult(null)}
        onReset={() => setResult(null)}
      />
      {result !== null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {result.kind === 'active' && <Alert severity="success">{t('check.active')}</Alert>}
          {result.kind === 'earlier' && <Alert severity="warning">{t('check.earlier', { status: result.status })}</Alert>}
          {result.kind === 'unknown' && <Alert severity="error">{t('check.unknown')}</Alert>}
          {result.warning !== null && <Alert severity="warning">{result.warning}</Alert>}
          <Fingerprint value={result.fingerprint} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{result.keyId}</Typography>
        </Box>
      )}
    </KeyDialogFrame>
  );
};

export const ReleaseKeyPage = () => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [keys, setKeys] = useState<ReleaseKey[] | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const canWrite = hasPermission(permissions as string[] | null | undefined, 'kiosk-release-keys:write:all');

  const [checkKeys, setCheckKeys] = useState<ReleaseKey[] | null>(null);
  const checkGeneration = useRef(0);

  const load = useCallback(async (): Promise<ReleaseKey[] | null> => {
    try {
      const answer = await apiClient.get<{ data: ReleaseKey[] }>(BASE);
      setKeys(answer.data);
      setLoadProblem(null);
      return answer.data;
    } catch (error) {
      setLoadProblem(keyErrorLine(error, translate));
      return null;
    }
  }, [translate]);

  const openCheck = async () => {
    const generation = checkGeneration.current + 1;
    checkGeneration.current = generation;
    setCheckKeys(null);
    setDialog('check');
    const fresh = await load();
    if (checkGeneration.current === generation) setCheckKeys(fresh);
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let live = true;
    ed25519Available().then((answer) => {
      if (live) setAvailable(answer);
    });
    return () => {
      live = false;
    };
  }, []);

  const active = keys === null ? undefined : keys.find((key) => key.status === 'active');
  const earlier = keys === null ? [] : keys.filter((key) => key.status !== 'active');
  const actionsDisabled = available !== true;
  const close = () => setDialog(null);
  const closeCheck = () => {
    checkGeneration.current += 1;
    setCheckKeys(null);
    setDialog(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, md: 2 } }}>
      <Typography variant="h5">{t('name')}</Typography>
      {available === false && <Alert severity="error">{t('errors.ed25519_unavailable')}</Alert>}
      {loadProblem !== null && <Alert severity="error">{loadProblem}</Alert>}
      {keys !== null && keys.length === 0 && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1">{t('none')}</Typography>
            {canWrite && (
              <Box>
                <Button variant="contained" disabled={actionsDisabled} onClick={() => setDialog('create')} sx={{ textTransform: 'none' }}>{t('actions.create')}</Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {active !== undefined && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6">{t('active')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('fingerprint')}</Typography>
            <Fingerprint value={active.fingerprint} />
            <Row label={t('keyId')} value={<Box component="span" sx={{ fontFamily: 'monospace' }}>{active.keyId}</Box>} />
            <Row label={t('createdAt')} value={when(active.createdAt)} />
            <Row label={t('createdBy')} value={person(active.createdBy, active.createdByEmail)} />
            {active.replacedReason !== null && <Row label={t('replacedReason')} value={active.replacedReason} />}
            {canWrite && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mt: 1 }}>
                <Button variant="contained" disabled={actionsDisabled} onClick={() => setDialog('rotate')} sx={{ textTransform: 'none' }}>{t('actions.rotate')}</Button>
                <Button variant="outlined" color="error" disabled={actionsDisabled} onClick={() => setDialog('replace')} sx={{ textTransform: 'none' }}>{t('actions.replace')}</Button>
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" disabled={actionsDisabled} onClick={openCheck} sx={{ textTransform: 'none' }}>{t('actions.checkFile')}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {earlier.length > 0 && (
        <Card sx={{ minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('history')}</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('keyId')}</TableCell>
                    <TableCell>{translate('resources.kiosk-releases.columns.status')}</TableCell>
                    <TableCell>{t('createdAt')}</TableCell>
                    <TableCell>{t('retiredAt')}</TableCell>
                    <TableCell>{t('retiredBy')}</TableCell>
                    <TableCell>{t('replacedReason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earlier.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{key.keyId}</TableCell>
                      <TableCell>{t(`statuses.${key.status}`)}</TableCell>
                      <TableCell>{when(key.createdAt)}</TableCell>
                      <TableCell>{when(key.retiredAt)}</TableCell>
                      <TableCell>{person(key.retiredBy, key.retiredByEmail)}</TableCell>
                      <TableCell>{key.replacedReason === null ? '-' : key.replacedReason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}
      {dialog === 'create' && <CreateDialog onClose={close} onChanged={load} />}
      {dialog === 'rotate' && active !== undefined && <RotateDialog active={active} onClose={close} onChanged={load} />}
      {dialog === 'replace' && active !== undefined && <ReplaceDialog active={active} onClose={close} onChanged={load} />}
      {dialog === 'check' && <CheckDialog keys={checkKeys} problem={loadProblem} onClose={closeCheck} />}
    </Box>
  );
};
